import { useState, useEffect } from "react";
import { PDFDocument } from "pdf-lib";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font,
  pdf,
} from "@react-pdf/renderer";
import { v4 } from "uuid";
import Dots from "./dot-leaders.png";

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf",
    },
    {
      src: "http://fonts.gstatic.com/s/roboto/v15/bdHGHleUa-ndQCOrdpfxfw.ttf",
      fontWeight: "bold",
    },
  ],
});

const styles = StyleSheet.create({
  roboto: {
    fontFamily: "Roboto",
    fontWeight: "bold",
    fontSize: "15px",
    lineHeight: "calc(1em + 5px)",
    textTransform: "uppercase",
  },
  page: {
    padding: "61px 65px",
  },
  "table-container": {
    padding: "26px 0",
    display: "flex",
    flexDirection: "column",
    gap: "500px",
  },
  flex: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    overflow: "hidden",
    width: "100%",
    marginBottom: "16px",
  },
  dots: {
    overflow: "hidden",
    paddingRight: "10px",
    paddingBottom: "2.5px",
    objectFit: "contain",
    flexGrow: 1,
    width: "0",
  },
  text: {
    paddingRight: "10px",
    maxLines: 1,
    fontSize: "10px",
    lineHeight: "calc(1em + 2px)",
  },
});

/**
 * Function that returns a compiled PDF file from component parts and existing files.
 *
 * @param {JSX.Element} pdfParts - React component to render.
 * @param {Array<String>} pdfs - Array of PDF files.
 * @param {string} docTitle - Title of the document.
 * @param {Object[]} tocTitle - Optional object with section titles which also determines if the Table of Contents is generated and prepended.
 * @param {string} tocTitle[].docTitle - Document header.
 * @param {Array.<string>} tocTitle[].sections -  Section names to append. First in array is the pdfParts document name.
 */
export function usePdf(pdfParts, pdfs, docTitle, tocTitle) {
  const [document, setDocument] = useState({ file: null, pages: null });
  const [documentObject, setDocumentObject] = useState(null);

  // Document components to render.
  const returnPdfDoc = async (components) => {
    const doc = await pdf(components)
      .toBlob()
      .then((blob) => blob.arrayBuffer())
      .then((buffer) => PDFDocument.load(buffer));

    return {
      baseDoc: doc,
      pageSection: tocTitle
        ? {
            section: tocTitle.sections[0],
            ref: doc.getPage(0).ref,
            pages: doc.getPageCount(),
          }
        : null,
    };
  };

  // Add existing PDFs to the base document to render.
  useEffect(() => {
    let isSubscribed = true;

    let _pageSections = [];

    const returnDoc = async (doc, index) => {
      const _doc = await fetch(doc).then((res) => res.arrayBuffer());
      const retDoc = await PDFDocument.load(_doc);
      if (tocTitle) {
        _pageSections.push({
          section: tocTitle.sections[index],
          ref: retDoc.getPage(0).ref,
          pages: retDoc.getPageCount(),
        });
      }
      return retDoc;
    };

    const embedAll = async (docArray) => {
      const { baseDoc, pageSection } = await returnPdfDoc(pdfParts);
      if (pageSection) _pageSections.push(pageSection);

      const embed = async (pdfObject, isPrepend) => {
        const pages = await baseDoc.copyPages(
          pdfObject,
          pdfObject.getPageIndices()
        );

        for await (const [index, page] of pages.entries()) {
          baseDoc.insertPage(
            isPrepend ? 0 + index : baseDoc.getPageCount(),
            page
          );
        }
      };

      for await (const [index, file] of docArray.entries()) {
        const pdfSrc = await returnDoc(file, index + 1);
        embed(pdfSrc);
      }

      const returnToc = async (sections) => {
        const tocParts = (
          <Document>
            <Page size="LETTER" style={styles.page}>
              <View>
                <Text style={styles.roboto}>Placeholder</Text>
              </View>
              <View style={styles["table-container"]}>
                {sections.map((_) => (
                  <Text key={v4()}>Placeholder</Text>
                ))}
              </View>
            </Page>
          </Document>
        );

        let pageNumber = (await returnPdfDoc(tocParts)).pageSection.pages;

        const newToc = await returnPdfDoc(
          <Document>
            <Page size="LETTER" style={styles.page}>
              <View>
                <Text style={styles.roboto}>{tocTitle.docTitle}</Text>
              </View>
              <View style={styles["table-container"]}>
                {sections.map((section) => {
                  const retSect = (
                    <View style={styles.flex} key={v4()}>
                      <Text style={styles.text}>{section.section}</Text>
                      <Image style={styles.dots} src={Dots} alt="" />
                      <Text style={styles.text}>Page {pageNumber + 1}</Text>
                    </View>
                  );

                  pageNumber += section.pages;
                  return retSect;
                })}
              </View>
            </Page>
          </Document>
        );

        return newToc;
      };

      if (tocTitle) {
        const toc = await returnToc(_pageSections);
        await embed(toc.baseDoc, true);
      }

      return baseDoc;
    };

    embedAll(pdfs).then((pdf) => {
      pdf.setTitle(docTitle);
      pdf.save().then((bytes) => {
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        if (isSubscribed) {
          setDocument({
            file: url,
            pages: pdf.getPageIndices(),
          });
          setDocumentObject({ blob: blob, url: url, array: bytes });
        }
      });
    });

    return () => {
      isSubscribed = false;
    };
  }, []);

  return { document, documentObject };
}
